import { combineReducers, configureStore, isPlain, ThunkMiddleware } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, REGISTER, PERSIST, PAUSE, PURGE } from 'redux-persist';
// For localStorage (default)
import storage from 'redux-persist/lib/storage';
// For sessionStorage
import sessionStorage from 'redux-persist/lib/storage/session';
import authReducer from './reducers/getAuthReducer';
import menuReducer from './reducers/getMenu';
import profileReducer from './reducers/getProfile';
import incomeUnitReducer from './reducers/getIncomeUnit';
import incomeAttributesReducer from './reducers/getIncomeAttributes';
import fileReducer from './reducers/getFile';
import saveDataReducer from './reducers/getSaveData';
import readOnlyReducer from './reducers/getReadOnly';
import snapshotTypeDisplayReducer from './reducers/getSnapshotTypeDisplay';
import lookupsReducer from './reducers/getLookups';
import campaignReducer from './reducers/getCampaign';
import commentReducer from './reducers/getComment';
import acknowledgementReducer from './reducers/updateAcknowledgement';
import grainElevatorAssessmentInfoReducer from './reducers/getGrainElevatorAssessmentInfo';
import aggregateSiteAssessmentInfoReducer from './reducers/getAggregateSiteAssessmentInfo';
import loadingReducer from './reducers/getLoadingReducer';
import snapshotDataReducer from './reducers/getSnapshot';
import summaryDataReducer from './reducers/getSummaryData';
import commercialReconciliationReducer from './reducers/getCommercialReconciliation';
import pieAnalysisCalculationReducer from './reducers/getPIEAnalysisCalculation';
import { PersistPartial } from 'redux-persist/es/persistReducer';
import { AnyAction } from 'redux';
import apiMiddleware from './middleware/api';
import propertiesReducer from './reducers/getProperties';
import documentViewerReducer from './reducers/getDocumentViewer';

const localStorageReducer = combineReducers({
    authReducer,
});

const sessionStorageReducer = combineReducers({
    menuReducer,
    profileReducer,
    propertiesReducer,
    lookupsReducer,
    readOnlyReducer
});

const otherReducer = combineReducers({
    snapshotTypeDisplayReducer,
    incomeAttributesReducer,
    saveDataReducer,
    incomeUnitReducer,
    fileReducer,
    campaignReducer,
    commentReducer,
    acknowledgementReducer,
    grainElevatorAssessmentInfoReducer,
    aggregateSiteAssessmentInfoReducer,
    loadingReducer,
    snapshotDataReducer,
    summaryDataReducer,
    commercialReconciliationReducer,
    pieAnalysisCalculationReducer,
    documentViewerReducer
});

const currentVersion = 4; // state version: increment to force REHYDRATE of state

export const localPersistConfig = {
    key: 'local',
    storage: storage,
    version: currentVersion,
    // eslint-disable-next-line
    migrate: (state: any) => {
        const oldVersion = state?._persist?.version ?? 0;
        if (oldVersion < currentVersion) {
            return Promise.resolve(null);
        }
        return Promise.resolve(state);
    }
    // whitelist: [] -- persist everything in localStorageReducer to localStorage
};

export const sessionPersistConfig = {
    key: 'session',
    storage: sessionStorage,
    version: currentVersion,
    // eslint-disable-next-line
    migrate: (state: any) => {
        const oldVersion = state?._persist?.version ?? 0;
        if (oldVersion < currentVersion) {
            return Promise.resolve(null);
        }
        return Promise.resolve(state);
    }
    // whitelist: [] -- persist everything in sessionStorageReducer to sessionStorage
};

const persistedLocalReducer = persistReducer(localPersistConfig, localStorageReducer) as unknown as typeof localStorageReducer & PersistPartial;
const persistedSessionReducer = persistReducer(sessionPersistConfig, sessionStorageReducer) as unknown as typeof sessionStorageReducer & PersistPartial;


const combinedReducer = combineReducers({
    local: persistedLocalReducer,
    session: persistedSessionReducer,
    other: otherReducer // reducers not stored in session or local storage
});


export type RootState = ReturnType<typeof combinedReducer>;

export const rootReducer = (state: RootState | undefined, action: AnyAction) => {
    if (action.type === "RESET_ROOT") {
        state = undefined;
    }
    return combinedReducer(state, action);
}


const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        isSerializable: (value: unknown): boolean => {
          return (
            isPlain(value) ||
            (typeof FormData !== 'undefined' && value instanceof FormData)
          )
        },
      },
      immutableCheck: false, // Disable the immutability check
    }).concat(apiMiddleware as ThunkMiddleware),
})

const persistedStore = persistStore(store);
export default store;
export { persistedStore };
